/**
 * Century Wholesales - enquiry-list state on every product surface. 2026-09-04.
 *
 * The store takes no payment: the cart is an enquiry list that is handed to WhatsApp or
 * email from the cart drawer. This module makes that state legible on the product itself,
 * which the cart alone cannot do - a buyer scanning a collection could not tell what was
 * already on their list.
 *
 * When a variant is on the list the add control is replaced by two things and only two:
 * an "Added to enquiry" marker, and a single "Remove from enquiry" action. That is the
 * whole interaction surface by design (Sam, 2026-09-04) - quantity stays editable in the
 * enquiry drawer, which is the one place a number should be changed.
 *
 * Sync strategy is deliberately belt-and-braces. Horizon's own cart events are the fast
 * path, but the theme's add-to-cart, the drawer's remove buttons and the quantity steppers
 * all reach the cart through fetch(), so fetch is wrapped as the ground truth. Anything
 * that touches /cart/* triggers a re-read of /cart.js. That survives a theme update that
 * renames the internal events.
 */

/** @type {any} */
let CartLinesUpdateEvent = null;
/** @type {any} */
let StandardEvents = null;

try {
  const mod = await import('@shopify/events');
  CartLinesUpdateEvent = mod.CartLinesUpdateEvent ?? null;
  StandardEvents = mod.StandardEvents ?? null;
} catch (_) {
  // Standard events bundle unavailable - the fetch wrapper below still keeps state correct.
}

const TEXT = {
  added: 'Added to enquiry',
  remove: 'Remove from enquiry',
  removing: 'Removing…',
};

const routes = () => (typeof window.Theme !== 'undefined' && window.Theme.routes) || {};
const cartUrl = () => routes().cart_url || '/cart';
const changeUrl = () => routes().cart_change_url || '/cart/change.js';

/** variant id (string) -> cart line key */
let lines = new Map();

/** Seeded by cw-enquiry-state.liquid so the first paint is already correct. */
function seed() {
  const initial = window.CWEnquiry && window.CWEnquiry.lines;
  if (!Array.isArray(initial)) return;
  lines = new Map(initial.map((line) => [String(line.variant_id), line.key]));
}

function setLines(items) {
  lines = new Map((items || []).map((item) => [String(item.variant_id), item.key]));
}

async function refresh() {
  try {
    const response = await fetch(cartUrl() + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    const cart = await response.json();
    setLines(cart.items);
  } catch (_) {
    // Leave the last known state rather than flashing every product back to "add".
  }
  render();
}

/* -------------------------------------------------------------------- rendering */

const CHECK =
  '<svg class="cw-inq__tick" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
  '<circle cx="10" cy="10" r="9" fill="currentColor"/>' +
  '<path d="m6 10.3 2.6 2.6L14.2 7.3" stroke="#FFFFFF" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * @param {string} variantId
 * @param {boolean} isCard
 * @returns {HTMLElement}
 */
function buildState(variantId, isCard) {
  const wrapper = document.createElement('div');
  wrapper.className = isCard ? 'cw-inq cw-inq--card' : 'cw-inq';
  wrapper.dataset.cwVariant = variantId;

  // Only the static tick is markup; the variant id reaches the DOM through dataset and
  // every label through textContent, so nothing here can be an injection point.
  const added = document.createElement('p');
  added.className = 'cw-inq__added';
  added.setAttribute('role', 'status');
  added.innerHTML = CHECK;
  const label = document.createElement('span');
  label.textContent = TEXT.added;
  added.append(label);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'cw-inq__remove';
  button.dataset.variantId = variantId;
  button.textContent = TEXT.remove;

  wrapper.append(added, button);
  return wrapper;
}

/**
 * Every add-to-cart form on the page, paired with the element the state block mounts into.
 */
function surfaces() {
  const found = [];
  for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
    const input = form.querySelector('input[name="id"]');
    if (!input || !input.value) continue;

    // The quick-add modal renders a second form for the same product inside a dialog that
    // is torn down on close, so it is skipped rather than decorated.
    if (form.closest('quick-add-modal-component, dialog.quick-add-modal')) continue;

    const card = form.closest('product-card');
    const host = card
      ? card.querySelector('.product-card__content')
      : form.closest('.buy-buttons-block');
    if (!host) continue;

    found.push({ variantId: String(input.value), host, card });
  }
  return found;
}

let rendering = false;

function render() {
  if (rendering) return;
  rendering = true;
  observer.disconnect();

  try {
    const live = new Set();

    for (const { variantId, host, card } of surfaces()) {
      const onList = lines.has(variantId);
      const existing = host.querySelector(':scope > .cw-inq');

      if (!onList) {
        if (existing) existing.remove();
        (card || host).classList.remove('cw-inq-on-list');
        continue;
      }

      live.add(host);
      (card || host).classList.add('cw-inq-on-list');

      if (existing) {
        if (existing.dataset.cwVariant !== variantId) {
          existing.replaceWith(buildState(variantId, Boolean(card)));
        }
        continue;
      }
      host.append(buildState(variantId, Boolean(card)));
    }

    // Clear anything left behind by a section morph that swapped the host out from under us.
    for (const stale of document.querySelectorAll('.cw-inq')) {
      const host = stale.parentElement;
      if (!host || live.has(host)) continue;
      stale.remove();
      const card = host.closest('product-card');
      if (card) card.classList.remove('cw-inq-on-list');
      host.classList.remove('cw-inq-on-list');
    }
  } finally {
    observer.observe(document.body, { childList: true, subtree: true });
    rendering = false;
  }
}

/* ----------------------------------------------------------------------- removal */

/**
 * @param {string} variantId
 * @param {HTMLButtonElement} button
 */
async function removeLine(variantId, button) {
  const key = lines.get(variantId);
  if (!key) return;

  button.disabled = true;
  button.textContent = TEXT.removing;

  // Ask for every cart section on the page so the drawer and the cart page re-render from
  // the same response, exactly as component-cart-items.js does for its own remove button.
  const sectionIds = new Set();
  for (const node of document.querySelectorAll('cart-items-component[data-section-id]')) {
    sectionIds.add(node.dataset.sectionId);
  }

  const body = JSON.stringify({
    id: key,
    quantity: 0,
    sections: Array.from(sectionIds).join(','),
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent ? CartLinesUpdateEvent.createPromise() : null;
  if (deferred) {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: key, quantity: 0 }],
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body,
    });
    const data = await response.json();
    if (data.errors) throw new Error(String(data.errors));

    setLines(data.items);

    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          items: data.items,
          itemCount: data.item_count,
          source: 'cw-enquiry',
          didError: false,
        },
      });
    } else {
      updateBubbleDirectly(data.item_count);
    }

    render();
  } catch (error) {
    if (deferred) deferred.reject(error);
    button.disabled = false;
    button.textContent = TEXT.remove;
    console.warn('[cw-enquiry] Could not remove line:', error);
  }
}

/**
 * Fallback for when the standard events bundle is missing: nudge the header count itself.
 * @param {number} count
 */
function updateBubbleDirectly(count) {
  const icon = /** @type {any} */ (document.querySelector('cart-icon'));
  if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false);
}

/* ------------------------------------------------------------------------ wiring */

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  // "Add more products" navigates away; Horizon would restore the open drawer on the
  // next page (theme-drawer.liquid reads sessionStorage), covering the products the buyer
  // just asked to see. Forget the open state before the navigation happens.
  if (target && target.closest && target.closest('.cw-quote__btn--browse')) {
    try { sessionStorage.removeItem('theme-drawer-open'); } catch (_) { /* private mode */ }
    return;
  }
  const button = target && target.closest ? target.closest('.cw-inq__remove') : null;
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  removeLine(button.dataset.variantId || '', /** @type {HTMLButtonElement} */ (button));
});

let pending = 0;
function scheduleRender() {
  window.clearTimeout(pending);
  pending = window.setTimeout(render, 80);
}

const observer = new MutationObserver(scheduleRender);

// fetch is the ground truth: the theme's add-to-cart, the drawer steppers and the drawer's
// own remove button all go through /cart/*, whatever they call their events internally.
const nativeFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const response = await nativeFetch(...args);
  try {
    const input = args[0];
    const url = String(typeof input === 'string' ? input : (input && input.url) || '');
    if (/\/cart\/(add|change|update|clear)/.test(url)) window.setTimeout(refresh, 30);
  } catch (_) {
    // never let bookkeeping break a real request
  }
  return response;
};

if (StandardEvents && StandardEvents.cartLinesUpdate) {
  document.addEventListener(StandardEvents.cartLinesUpdate, () => window.setTimeout(refresh, 30));
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refresh();
});

seed();
render();
observer.observe(document.body, { childList: true, subtree: true });
// The seed is server-rendered and can be stale on a cached page; confirm against /cart.js.
refresh();
